import type { ArgoCDImageUpdateProperties } from "@octopusdeploy/legacy-action-properties";
import { GitCommitMethods } from "@octopusdeploy/legacy-action-properties";
import type { ActionProperties, ProjectResource, ProcessType } from "@octopusdeploy/octopus-server-client";
import { ActionExecutionLocation, GetNamedPackageReferences } from "@octopusdeploy/octopus-server-client";
import type { PropsWithChildren } from "react";
import * as React from "react";
import { useOptionalProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { TargetRoles } from "~/areas/projects/components/Process/types";
import { ActionInputs, isProjectOrProcessTemplateActionInput } from "~/components/ActionInputs/ActionInputs";
import type { ActionSummaryProps } from "~/components/Actions/actionSummaryProps";
import { ArgoCDApplicationsPreviewFormSection } from "~/components/Actions/argocd/ApplicationsPreview/ArgoCDApplicationsPreviewFormSection";
import { ArgoCDGitCommitMessageFormSection } from "~/components/Actions/argocd/ArgoCDGitCommitMessageFormSection";
import { ArgoCDGitCommitMethodFormSection } from "~/components/Actions/argocd/ArgoCDGitCommitMethodFormSection";
import type { ActionWithFeeds } from "~/components/Actions/commonActionHelpers";
import type { ActionEditProps } from "~/components/Actions/pluginRegistry";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import type { ScriptPackageProperties } from "~/components/Actions/script/scriptPackageReference";
import { BaseComponent } from "~/components/BaseComponent/BaseComponent";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import ExpanderSectionHeading from "~/components/form/Sections/FormSectionHeading";
import Summary from "~/components/form/Sections/Summary";
import { Note } from "~/components/form/index";
class ArgoCDUpdateApplicationImageTagsSummary extends BaseComponent<ActionSummaryProps, never> {
    constructor(props: ActionSummaryProps) {
        super(props);
    }
    render() {
        return <div>Update ArgoCD applications with new container image versions.</div>;
    }
    static displayName = "ArgoCDUpdateApplicationImageTagsSummary";
}
type ArgoCDImageUpdateActionProps = ActionEditProps<ArgoCDImageUpdateProperties, ScriptPackageProperties>;
type ArgoCDInfrastructureMetadata = {
    processType?: ProcessType;
};
export interface ArgoCDImageUpdateActionInternalProps extends ArgoCDImageUpdateActionProps, ActionWithFeeds, ArgoCDInfrastructureMetadata {
    project?: ProjectResource;
}
export class ArgoCDUpdateApplicationImageTagsEdit extends BaseComponent<ArgoCDImageUpdateActionInternalProps, {}> {
    constructor(props: ArgoCDImageUpdateActionInternalProps) {
        super(props);
    }
    packageReferenceSummary = () => {
        const namedPackageReferences = GetNamedPackageReferences(this.props.packages);
        if (namedPackageReferences.length === 0) {
            return Summary.placeholder("No additional packages referenced");
        }
        return Summary.summary(`${namedPackageReferences.length} package references`);
    };
    render() {
        // replace the default setPackages, to enforce no packages acquisition for this step.
        const { setPackages, ...rest } = this.props;
        return (<div>
                <ExpanderSectionHeading title="Input" description="When this step runs, Octopus will update the specified image tag in your configuration files."/>
                <ExpandableFormSection title={"Container Images"} isExpandedByDefault={this.props.expandedByDefault} errorKey="Octopus.Action.Script.Packages" summary={this.packageReferenceSummary()} help={"Add the container image(s) that Octopus will update during deployment."}>
                    <Note>
                        Learn more about <ExternalLink href="ScriptStepPackageReferences">package/container references</ExternalLink>.
                    </Note>

                    <ActionInputs actionUIDefinition={[{ type: "container-image-reference-list", key: "container-image-reference-list", hideAcquisitionChoices: true, hasRelatedTriggersDependencies: true }]} dependencies={this.props.actionInputsDependencies}/>
                </ExpandableFormSection>
                <>
                    <ExpanderSectionHeading title="Output" description="A new version of your configuration files with the updated image tag will be commited to Git, which will trigger a sync from Argo CD."/>
                    <ArgoCDApplicationsPreviewFormSection expandedByDefault={this.props.expandedByDefault}/>
                    <ArgoCDGitCommitMessageFormSection messageSummary={this.props.properties["Octopus.Action.ArgoCD.CommitMessageSummary"]} updateMessageSummary={(summary) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMessageSummary"]: summary })} messageSummaryError={this.props.getFieldError("Octopus.Action.ArgoCD.CommitMessageSummary")} messageDescription={this.props.properties["Octopus.Action.ArgoCD.CommitMessageDescription"]} updateMessageDescription={(description) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMessageDescription"]: description })} messageDescriptionError={this.props.getFieldError("Octopus.Action.ArgoCD.CommitMessageDescription")} localNames={this.props.localNames} expandedByDefault={this.props.expandedByDefault} messageDescriptionHelp={"Octopus will automatically add image version details to the description."}/>
                    <ArgoCDGitCommitMethodFormSection commitMethod={this.props.properties["Octopus.Action.ArgoCD.CommitMethod"]} updateCommitMethod={(val) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMethod"]: val })} commitMethodError={this.props.getFieldError("Octopus.Action.ArgoCD.CommitMethod")} environments={this.props.environments} processType={this.props.processType} environmentsValue={this.props.properties["Octopus.Action.ArgoCD.PullRequest.Environments"]} updateEnvironmentsValue={(e) => this.props.setProperties({ ["Octopus.Action.ArgoCD.PullRequest.Environments"]: e })} updateEnvironmentsTemplateParameterValue={(e) => this.props.setProperties({ ["Octopus.Action.ArgoCD.PullRequest.EnvironmentsTemplateParameter"]: e })} environmentsTemplateParameter={this.props.properties["Octopus.Action.ArgoCD.PullRequest.EnvironmentsTemplateParameter"]} expandedByDefault={this.props.expandedByDefault} persistenceSettings={this.props.project?.PersistenceSettings} onParameterAdded={isProjectOrProcessTemplateActionInput(this.props.actionInputsDependencies) ? this.props.actionInputsDependencies.onParameterAdded : undefined}/>
                </>
            </div>);
    }
    static displayName = "ArgoCDUpdateApplicationImageTagsEdit";
}
export function ArgoCDUpdateApplicationImageTags(props: PropsWithChildren<ArgoCDImageUpdateActionProps>) {
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const processContext = useOptionalProcessContext();
    return <ArgoCDUpdateApplicationImageTagsEdit {...props} feeds={feeds} processType={processContext?.selectors.getProcessType()} refreshFeeds={refreshFeeds}/>;
}
const defaultCommitMessageSummary = "Octopus Deploy updated image versions";
const InitialCommitSummary: ActionProperties = {
    "Octopus.Action.ArgoCD.CommitMessageSummary": defaultCommitMessageSummary,
};
const InitialCommitMethod: ActionProperties = {
    "Octopus.Action.ArgoCD.CommitMethod": GitCommitMethods.DirectCommit,
};
pluginRegistry.registerAction({
    executionLocation: ActionExecutionLocation.AlwaysOnServer,
    canRunOnWorker: true,
    canRunInContainer: true,
    disableInlineExecutionContainers: true,
    actionType: "Octopus.ArgoCDUpdateImageTags",
    summary: (properties, targetRolesAsCSV) => <ArgoCDUpdateApplicationImageTagsSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV}/>,
    editSections: {
        //top: (props: ActionEditProps<UpdateImageVersionProperties>) => ,
        default: (props: ArgoCDImageUpdateActionProps) => <ArgoCDUpdateApplicationImageTags {...props}/>,
    },
    getInitialProperties: () => ({
        ...InitialCommitSummary,
        ...InitialCommitMethod,
    }),
    canHaveChildren: (step) => false,
    canBeChild: true,
    targetRoleOption: (action) => TargetRoles.None,
    hasPackages: (action) => true,
    docsLink: "updateArgoCDApplicationImageTags",
});
